import { useAuthStore } from "@persisted-state/auth/auth.store";

export const MOVEMENT_SERVICE_STATUSES = {
  PENDING_APPROVAL: "Pending Approval",
  AWAITING_MARINE_DEPARTMENT_APPROVAL: "AWAITING_MARINE_DEPARTMENT_APPROVAL",
  AWAITING_ARRIVAL_TERMINAL_APPROVAL: "AWAITING_ARRIVAL_TERMINAL_APPROVAL",
  AWAITING_DEPARTURE_TERMINAL_APPROVAL: "AWAITING_DEPARTURE_TERMINAL_APPROVAL",
  REJECTED_BY_DEPARTURE_TERMINAL: "REJECTED_BY_DEPARTURE_TERMINAL",
  REJECTED_BY_ARRIVAL_TERMINAL: "REJECTED_BY_ARRIVAL_TERMINAL",
  REJECTED_BY_MARINE_DEPARTMENT: "REJECTED_BY_MARINE_DEPARTMENT",
  MOVEMENT_STARTED: "MOVEMENT_STARTED",
  APPROVED: "APPROVED",
  COMPLETED: "COMPLETED",
  CLOSED: "CLOSED",
  CANCELLED: "CANCELLED",
  CANCELLED_BY_SHIPPING_AGENT: "CANCELLED_BY_SHIPPING_AGENT",
  CANCELLED_BY_MARINE_DEPARTMENT: "CANCELLED_BY_MARINE_DEPARTMENT",
  REJECTED: "REJECTED",
};

export const TASK_STATUSES = {
  READY: "READY",
  CLAIMED: "CLAIMED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  TERMINATED: "TERMINATED",
};

export const CLAIMABLE_MOVEMENT_STATUSES = [
  MOVEMENT_SERVICE_STATUSES.PENDING_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_MARINE_DEPARTMENT_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_ARRIVAL_TERMINAL_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_DEPARTURE_TERMINAL_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.APPROVED,
  MOVEMENT_SERVICE_STATUSES.MOVEMENT_STARTED,
  MOVEMENT_SERVICE_STATUSES.REJECTED_BY_DEPARTURE_TERMINAL,
  MOVEMENT_SERVICE_STATUSES.REJECTED_BY_ARRIVAL_TERMINAL,
  MOVEMENT_SERVICE_STATUSES.REJECTED_BY_MARINE_DEPARTMENT,
];

const NON_APPROVED_MOVEMENT_STATUSES = [
  MOVEMENT_SERVICE_STATUSES.PENDING_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_MARINE_DEPARTMENT_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_ARRIVAL_TERMINAL_APPROVAL,
];

const DEPARTURE_TERMINAL_APPROVAL = [
  MOVEMENT_SERVICE_STATUSES.AWAITING_DEPARTURE_TERMINAL_APPROVAL,
];

const ARRIVAL_TERMINAL_APPROVAL = [MOVEMENT_SERVICE_STATUSES.AWAITING_ARRIVAL_TERMINAL_APPROVAL];
const PRE_APPROVED_MOVEMENT_STATUSES = [
  MOVEMENT_SERVICE_STATUSES.APPROVED,
  MOVEMENT_SERVICE_STATUSES.MOVEMENT_STARTED,
];
const NON_DISPUTED_MOVEMENT_STATUSES = [
  MOVEMENT_SERVICE_STATUSES.AWAITING_DEPARTURE_TERMINAL_APPROVAL,
];

export const CURRENT_MOVEMENT_STATUSES = [
  MOVEMENT_SERVICE_STATUSES.PENDING_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_MARINE_DEPARTMENT_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_ARRIVAL_TERMINAL_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_DEPARTURE_TERMINAL_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.APPROVED,
];

export const PAST_MOVEMENT_STATUSES = [
  MOVEMENT_SERVICE_STATUSES.COMPLETED,
  MOVEMENT_SERVICE_STATUSES.CLOSED,
  MOVEMENT_SERVICE_STATUSES.CANCELLED,
  MOVEMENT_SERVICE_STATUSES.REJECTED,
];

const CANCELABLE_BY_SHIPPING_AGENT = [
  MOVEMENT_SERVICE_STATUSES.AWAITING_MARINE_DEPARTMENT_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_ARRIVAL_TERMINAL_APPROVAL,
  MOVEMENT_SERVICE_STATUSES.AWAITING_DEPARTURE_TERMINAL_APPROVAL,
];

const CANCELABLE_BY_SHIPPING_AGENT_WITH_CLAIM = [
  MOVEMENT_SERVICE_STATUSES.REJECTED_BY_ARRIVAL_TERMINAL,
  MOVEMENT_SERVICE_STATUSES.REJECTED_BY_DEPARTURE_TERMINAL,
  MOVEMENT_SERVICE_STATUSES.REJECTED_BY_MARINE_DEPARTMENT,
];

const CANCELABLE_BY_MARINE_DEPARTMENT = [
  MOVEMENT_SERVICE_STATUSES.APPROVED,
  MOVEMENT_SERVICE_STATUSES.MOVEMENT_STARTED,
];

const REJECTED_MOVEMENT_STATUSES = [
  MOVEMENT_SERVICE_STATUSES.REJECTED_BY_DEPARTURE_TERMINAL,
  MOVEMENT_SERVICE_STATUSES.REJECTED_BY_ARRIVAL_TERMINAL,
  MOVEMENT_SERVICE_STATUSES.REJECTED_BY_MARINE_DEPARTMENT,
];

export const CANCELLED_MOVEMENT_STATUSES = [
  MOVEMENT_SERVICE_STATUSES.CANCELLED,
  MOVEMENT_SERVICE_STATUSES.CANCELLED_BY_SHIPPING_AGENT,
  MOVEMENT_SERVICE_STATUSES.CANCELLED_BY_MARINE_DEPARTMENT,
];

// Movement service methods
export const isCurrentMovement = (status: string) => CURRENT_MOVEMENT_STATUSES.includes(status);
export const isPastMovement = (status: string) => PAST_MOVEMENT_STATUSES.includes(status);

const isMovementServiceClaimable = (status: string) => CLAIMABLE_MOVEMENT_STATUSES.includes(status);

const isMovementServiceApprovableOrRejectable = (status: string) =>
  NON_APPROVED_MOVEMENT_STATUSES.includes(status);

const isMovementServicePreApproved = (status: string) =>
  PRE_APPROVED_MOVEMENT_STATUSES.includes(status);

const isMovementServiceNonDisputable = (terminalReport: string) =>
  NON_DISPUTED_MOVEMENT_STATUSES.includes(terminalReport);

const isMovementServiceCancelableByShippingAgent = (status: string) =>
  CANCELABLE_BY_SHIPPING_AGENT.includes(status);

const isMovementServiceCancelableByShippingAgentWithClaim = (status: string) =>
  CANCELABLE_BY_SHIPPING_AGENT_WITH_CLAIM.includes(status);

const isMovementServiceCancelableByMarineDepartment = (status: string) =>
  CANCELABLE_BY_MARINE_DEPARTMENT.includes(status);

const isMovementServiceRejected = (status: string) => REJECTED_MOVEMENT_STATUSES.includes(status);

// Task methods
const isTaskNotClaimed = (taskStatus: string) => taskStatus === TASK_STATUSES.READY;
const isTaskClaimed = (taskStatus: string) => taskStatus === TASK_STATUSES.CLAIMED;

const isTaskOwner = (claimOwnerName: string) =>
  claimOwnerName === useAuthStore.getState().user?.preferredUsername;

const isUserClaimedTaskOwner = (taskStatus: string, claimOwnerName: string) =>
  isTaskClaimed(taskStatus) && isTaskOwner(claimOwnerName);

const isUserShippingAgent = () => {
  const userRoles = useAuthStore.getState().user?.roles || [];
  return userRoles.includes("shipping-agent");
};

const isUserMarineOperator = () => {
  const userRoles = useAuthStore.getState().user?.roles || [];
  return userRoles.includes("marine-operator");
};

const isUserMarineSupervisor = () => {
  const userRoles = useAuthStore.getState().user?.roles || [];
  return userRoles.includes("marine-supervisor");
};

export const isShippingAgentMovement = (submittedByRole?: string | null) =>
  submittedByRole === "shipping-agent";

export const isDepartureTerminalClaimedTaskOwner = (taskStatus: string) =>
  DEPARTURE_TERMINAL_APPROVAL.includes(taskStatus);

export const isArrivalTerminalClaimedTaskOwner = (taskStatus: string) =>
  ARRIVAL_TERMINAL_APPROVAL.includes(taskStatus);
// Flow methods
export const canUserClaim = (taskStatus: string, status: string) =>
  isTaskNotClaimed(taskStatus) && isMovementServiceClaimable(status);

export const canUserUnclaim = (taskStatus: string, claimOwnerName: string) =>
  isUserClaimedTaskOwner(taskStatus, claimOwnerName);

const isArrivalTerminal = (to: string) => {
  const userTerminal = useAuthStore.getState().user?.terminalId;
  return userTerminal !== undefined && userTerminal === to;
};

const isDepartureTerminal = (from: string) => {
  const userTerminal = useAuthStore.getState().user?.terminalId;
  return userTerminal !== undefined && userTerminal === from;
};
export const canUserApprove = (
  taskStatus: string,
  claimOwnerName: string,
  status: string,
  to: string,
) => {
  const hasTerminalId = useAuthStore.getState().user?.terminalId;

  return hasTerminalId
    ? isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
        isMovementServiceApprovableOrRejectable(status) &&
        isArrivalTerminal(to)
    : isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
        isMovementServiceApprovableOrRejectable(status);
};

export const canUserReject = (
  taskStatus: string,
  claimOwnerName: string,
  status: string,
  to: string,
) => {
  const hasTerminalId = useAuthStore.getState().user?.terminalId;

  return hasTerminalId
    ? isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
        isMovementServiceApprovableOrRejectable(status) &&
        isArrivalTerminal(to)
    : isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
        isMovementServiceApprovableOrRejectable(status);
};

export const canUserUpdate = (taskStatus: string, claimOwnerName: string, status: string) =>
  isUserClaimedTaskOwner(taskStatus, claimOwnerName) && isMovementServicePreApproved(status);

export const canUserResubmit = (taskStatus: string, claimOwnerName: string, status: string) =>
  isUserClaimedTaskOwner(taskStatus, claimOwnerName) && isMovementServiceRejected(status);

export const canUserClose = (taskStatus: string, claimOwnerName: string, status: string) => {
  const canCloseStatus =
    status === MOVEMENT_SERVICE_STATUSES.APPROVED ||
    status === MOVEMENT_SERVICE_STATUSES.MOVEMENT_STARTED;
  return isUserClaimedTaskOwner(taskStatus, claimOwnerName) && canCloseStatus;
};

export const canUserCancel = (
  taskStatus: string,
  claimOwnerName: string,
  status: string,
  submittedByRole?: string | null,
) => {
  const isShippingAgentMovementRequest = isShippingAgentMovement(submittedByRole);

  return (
    (isUserShippingAgent() &&
      isShippingAgentMovementRequest &&
      isMovementServiceCancelableByShippingAgent(status)) ||
    (isUserShippingAgent() &&
      isShippingAgentMovementRequest &&
      isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
      isMovementServiceCancelableByShippingAgentWithClaim(status)) ||
    (!isShippingAgentMovementRequest && (isUserMarineOperator() || isUserMarineSupervisor())) ||
    (isMovementServiceCancelableByMarineDepartment(status) &&
      (isUserMarineOperator() || isUserMarineSupervisor()))
  );
};

// Conditions to be updated when refined
export const canUserDispute = (
  taskStatus: string,
  claimOwnerName: string,
  from: string,
  status: string,
) =>
  isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
  isMovementServiceNonDisputable(status) &&
  isDepartureTerminal(from);

export const canUserNonDispute = (
  taskStatus: string,
  claimOwnerName: string,
  from: string,
  status: string,
) =>
  isUserClaimedTaskOwner(taskStatus, claimOwnerName) &&
  isMovementServiceNonDisputable(status) &&
  isDepartureTerminal(from);
